import React, { useEffect, useState } from 'react'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { RoomTypeRow } from '@/lib/types'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageSpinner, Textarea, useConfirm, useToast } from '@/components/ui'

const DEFAULTS = [
  { name: 'Single', default_capacity: 1 },
  { name: 'Double', default_capacity: 2 },
  { name: 'Triple', default_capacity: 3 },
  { name: 'Suite', default_capacity: 2 },
  { name: 'Royal Suite', default_capacity: 4 },
]

export default function RoomTypesPage() {
  const { event } = useEvent()
  const [types, setTypes] = useState<RoomTypeRow[] | null>(null)
  const [editing, setEditing] = useState<RoomTypeRow | 'new' | null>(null)
  const [seeding, setSeeding] = useState(false)
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  async function load() {
    const { data, error } = await supabase.from('room_types').select('*').eq('event_id', event.id).order('default_capacity')
    if (error) push(error.message, 'error')
    setTypes((data as RoomTypeRow[]) || [])
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id])

  async function seedDefaults() {
    setSeeding(true)
    const { error } = await supabase.from('room_types').insert(DEFAULTS.map((d) => ({ ...d, event_id: event.id })))
    setSeeding(false)
    if (error) push(error.message, 'error')
    else {
      push('Default room types added')
      load()
    }
  }

  async function remove(rt: RoomTypeRow) {
    const ok = await confirm({ title: `Delete ${rt.name}?`, description: 'Only possible if no allocation uses this room type yet.', tone: 'danger', confirmLabel: 'Delete' })
    if (!ok) return
    const { error } = await supabase.from('room_types').delete().eq('id', rt.id)
    if (error) {
      push('Could not delete: this room type is already used in an allocation.', 'error')
      return
    }
    push('Room type deleted')
    load()
  }

  if (types === null) return <PageSpinner label="Loading room types…" />

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Room Types</h1>
          <p className="text-sm text-slate-500">Configurable room categories. Capacity is set here, never hardcoded.</p>
        </div>
        <Button variant="primary" onClick={() => setEditing('new')}>
          + Add Room Type
        </Button>
      </div>

      {types.length === 0 ? (
        <EmptyState
          title="No room types yet"
          description="Define the room categories you'll allocate (Single, Double, Suite, ...) with their guest capacity."
          action={
            <>
              <Button variant="primary" onClick={() => setEditing('new')}>
                Add Room Type
              </Button>
              <Button variant="secondary" loading={seeding} onClick={seedDefaults}>
                Add Single/Double/Triple/Suite/Royal Suite
              </Button>
            </>
          }
        />
      ) : (
        <div className="space-y-2">
          {types.map((rt) => (
            <Card key={rt.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{rt.name}</span>
                  <Badge tone="blue">Capacity {rt.default_capacity}</Badge>
                  {!rt.is_active && <Badge tone="slate">Inactive</Badge>}
                </div>
                {rt.description && <div className="text-xs text-slate-500">{rt.description}</div>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing(rt)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(rt)}>
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <RoomTypeModal
          eventId={event.id}
          roomType={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
      {confirmDialog}
    </div>
  )
}

function RoomTypeModal({ eventId, roomType, onClose, onSaved }: { eventId: string; roomType: RoomTypeRow | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(roomType?.name || '')
  const [capacity, setCapacity] = useState(roomType?.default_capacity ?? 1)
  const [description, setDescription] = useState(roomType?.description || '')
  const [isActive, setIsActive] = useState(roomType?.is_active ?? true)
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  async function submit() {
    if (!name.trim() || capacity < 1) {
      push('Name and a capacity of at least 1 are required', 'error')
      return
    }
    setSaving(true)
    const payload = { event_id: eventId, name: name.trim(), default_capacity: capacity, description: description.trim() || null, is_active: isActive }
    const { error } = roomType ? await supabase.from('room_types').update(payload).eq('id', roomType.id) : await supabase.from('room_types').insert(payload)
    setSaving(false)
    if (error) {
      push(error.message, 'error')
      return
    }
    push(roomType ? 'Room type updated' : 'Room type added')
    onSaved()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={roomType ? 'Edit Room Type' : 'Add Room Type'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Double" />
        </Field>
        <Field label="Default Capacity" required hint="Guests per room. Can be overridden per allocation.">
          <Input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value) || 1))} />
        </Field>
        <Field label="Description">
          <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-600" />
          Active
        </label>
      </div>
    </Modal>
  )
}
